import React from "react";
import { toast } from "sonner";
import { Loader2, CheckCircle2, AlertCircle, FileText, ArrowRight } from "lucide-react";
import { businessApi } from "../../lib/api";
import { useNavigate, useLocation } from "react-router-dom";

export default function BusinessTermsAcceptancePage() {
  const nav = useNavigate();
  const loc = useLocation();
  const [terms, setTerms] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [fullName, setFullName] = React.useState("");
  const [roleTitle, setRoleTitle] = React.useState("");
  const [agreeRead, setAgreeRead] = React.useState(false);
  const [agreeAccept, setAgreeAccept] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);

  React.useEffect(() => {
    (async () => {
      try {
        const { data } = await businessApi().get("/b2b/2r5/terms/current");
        setTerms(data);
      } catch {
        toast.error("Kunne ikke hente betingelser");
      } finally { setLoading(false); }
    })();
  }, []);

  const accept = async () => {
    if (fullName.trim().length < 2 || roleTitle.trim().length < 2) {
      toast.error("Udfyld navn og rolle");
      return;
    }
    if (!agreeRead || !agreeAccept) {
      toast.error("Bekræft begge checkbokse");
      return;
    }
    setSubmitting(true);
    try {
      await businessApi().post("/b2b/2r5/terms/accept", {
        accepted_by_full_name: fullName.trim(),
        role_title: roleTitle.trim(),
      });
      toast.success("Tak — betingelser accepteret");
      const next = new URLSearchParams(loc.search).get("next") || "/business/dashboard";
      nav(next);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fejl ved accept");
    } finally { setSubmitting(false); }
  };

  if (loading) {
    return <div className="flex items-center gap-2 text-brand-text2"><Loader2 className="animate-spin" size={16}/> Indlæser…</div>;
  }
  if (!terms || !terms.current_version) {
    return <div className="rounded-lg border border-brand p-6 text-brand-text2">Ingen aktive B2B-betingelser at acceptere.</div>;
  }
  if (terms.accepted) {
    return (
      <div className="rounded-lg border border-green-200 bg-green-50 p-6" data-testid="terms-already-accepted">
        <CheckCircle2 className="text-green-700 mb-2" size={32}/>
        <h2 className="font-bold mb-1">Betingelser allerede accepteret</h2>
        <p className="text-sm text-brand-text2">Version {terms.current_version} blev accepteret af {terms.acceptance?.accepted_by_full_name} ({terms.acceptance?.role_title}) den {(terms.acceptance?.accepted_at_iso || "").slice(0, 10)}.</p>
        <button onClick={() => nav("/business/dashboard")} className="mt-3 px-4 py-2 rounded bg-brand-dark text-white text-sm">Til dashboard</button>
      </div>
    );
  }

  return (
    <div className="space-y-4 max-w-4xl" data-testid="business-terms-acceptance-page">
      <header>
        <div className="flex items-center gap-2 text-xs text-brand-text2">
          <FileText size={14}/> Version {terms.current_version}
        </div>
        <h1 className="text-2xl font-bold text-brand-dark">{terms.title}</h1>
        {terms.summary && <p className="text-sm text-brand-text2 mt-1">{terms.summary}</p>}
      </header>

      <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 flex items-start gap-2">
        <AlertCircle className="mt-0.5 flex-shrink-0" size={16}/>
        <div>For at oprette nye forsendelser skal du acceptere de aktuelle B2B-betingelser. Du behøver kun gøre det én gang pr. version.</div>
      </div>

      <div className="rounded-lg border border-brand bg-white p-6 max-h-[60vh] overflow-y-auto" data-testid="terms-body">
        <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: terms.body_html }}/>
      </div>

      <div className="rounded-lg border border-brand bg-brand-bg2/40 p-4 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-brand-text2">Fulde navn</label>
            <input className="input-flat w-full" value={fullName} onChange={(e) => setFullName(e.target.value)}
                   data-testid="terms-full-name" placeholder="Eks. Jens Hansen"/>
          </div>
          <div>
            <label className="text-xs text-brand-text2">Rolle/titel</label>
            <input className="input-flat w-full" value={roleTitle} onChange={(e) => setRoleTitle(e.target.value)}
                   data-testid="terms-role-title" placeholder="Eks. CEO, indkøbschef"/>
          </div>
        </div>
        <label className="flex items-start gap-2 text-sm">
          <input type="checkbox" checked={agreeRead} onChange={(e) => setAgreeRead(e.target.checked)}
                 data-testid="terms-agree-read" className="mt-1"/>
          <span>Jeg bekræfter, at jeg har læst og forstået EMA Kurer's B2B-betingelser (version {terms.current_version}).</span>
        </label>
        <label className="flex items-start gap-2 text-sm">
          <input type="checkbox" checked={agreeAccept} onChange={(e) => setAgreeAccept(e.target.checked)}
                 data-testid="terms-agree-accept" className="mt-1"/>
          <span>Jeg accepterer betingelserne på vegne af min virksomhed og bekræfter, at jeg har myndighed til at gøre dette.</span>
        </label>
        <button onClick={accept} disabled={submitting} data-testid="terms-accept-submit"
                className="px-5 py-2.5 rounded bg-brand-red text-white font-semibold disabled:opacity-50 flex items-center gap-2">
          {submitting && <Loader2 className="animate-spin" size={16}/>}
          Acceptér betingelser <ArrowRight size={16}/>
        </button>
      </div>
    </div>
  );
}
